import { useRef, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, ArrowRight, RefreshCw, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import MovieCard, { imdbCache } from "./MovieCard";
import imdbBatch from "../lib/imdbBatch";
import watchProvidersBatch from "../lib/watchProvidersBatch";
import { useSubscription } from "../lib/SubscriptionContext";

// v2.97o — Free tier rail-depth cap. Pro/Trial/Founder see all items.
const FREE_RAIL_CAP = 20;

export default function MovieCarousel({ title, subtitle, items: rawItems = [], testid, viewAllHref, badge, onRefresh, disableShuffle = false, showLessVariant = "hover", showPlatform = false, collectionSlug = null, totalCount = null }) {
  const ref = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const [spinning, setSpinning] = useState(false);
  // v2.97o — Read subscription state for free-tier rail capping.
  const { isPro, openPaywall } = useSubscription();
  // Local shuffle seed — increments on each shuffle press. Used to deterministically
  // re-order items (Fisher-Yates) when no `onRefresh` callback is wired (most rails).
  const [shuffleSeed, setShuffleSeed] = useState(0);

  // Re-order items based on shuffle seed (deterministic Fisher-Yates).
  // shuffleSeed=0 → return items as-is (server's original ranking).
  const items = useMemo(() => {
    if (shuffleSeed === 0 || !rawItems?.length) return rawItems;
    const arr = [...rawItems];
    // Seeded mulberry32 PRNG so each seed → reproducible shuffle order.
    let s = shuffleSeed * 2654435761 | 0;
    const rand = () => {
      s = (s + 0x6D2B79F5) | 0;
      let t = s;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, [rawItems, shuffleSeed]);

  // v2.97z — Hand off to the global ImdbBatchManager. Previously every
  // rail fired its own POST /api/imdb-bulk on mount. Now all rails on the
  // same paint cycle merge into ONE combined POST (100 ms debounce), so
  // home page IMDb-bulk traffic drops from 20+ requests to 1-3.
  // The batch manager itself handles imdbCache writes + the
  // `fm-imdb-cache-update` event — we just register and forget.
  useEffect(() => {
    if (!rawItems || rawItems.length === 0) return;
    // Pre-warm up to 80 so all visible For You / large rails get IMDb ratings.
    const slice = rawItems.slice(0, 80);
    imdbBatch.register(slice).catch(() => { /* graceful fall-through */ });
  }, [rawItems]);

  // v3.1 — Watch-providers batch pre-warm (only for showPlatform rails).
  // Collects all items from all showPlatform=true rows on the page into ONE
  // POST /api/watch-providers/bulk-cards (100ms debounce). Cache-only on the
  // backend — no TMDB API calls, always sub-10ms. Cards react via the
  // "fm-watch-providers-update" event.
  useEffect(() => {
    if (!showPlatform || !rawItems || rawItems.length === 0) return;
    // Skip theatre titles — they have a hardcoded "In Theatres" chip and must
    // never be overridden by OTT watch-provider badges from the batch fetch.
    const slice = rawItems.slice(0, 80).filter(it => it.platform_key !== "theatre");
    if (slice.length === 0) return;
    watchProvidersBatch.register(slice).catch(() => { /* graceful fall-through */ });
  }, [rawItems, showPlatform]);

  const updateArrows = () => {
    const el = ref.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    updateArrows();
    const onScroll = () => updateArrows();
    el.addEventListener("scroll", onScroll, { passive: true });
    const ro = new ResizeObserver(() => updateArrows());
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", onScroll);
      ro.disconnect();
    };
  }, [items, isPro]);

  const scroll = (dir) => {
    const el = ref.current;
    if (!el) return;
    const step = Math.max(300, el.clientWidth * 0.85);
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  const handleRefresh = async () => {
    if (spinning) return;
    setSpinning(true);
    try {
      if (onRefresh) {
        await onRefresh();
      } else {
        // No backend refresh wired → just locally re-shuffle the items.
        // Scroll the rail back to start so the user sees the new order.
        setShuffleSeed((s) => s + 1);
        if (ref.current) ref.current.scrollTo({ left: 0, behavior: "smooth" });
      }
    } finally {
      setTimeout(() => setSpinning(false), 600);
    }
  };

  // Show shuffle button when:
  //   - explicit onRefresh is provided (For You), OR
  //   - rail has >8 items and shuffle wasn't disabled (local Fisher-Yates).
  // Small rails (<8 items) don't benefit from shuffle.
  const showShuffle = !disableShuffle && (onRefresh || (rawItems?.length || 0) > 8);

  if (!items.length) return null;

  // v2.36 — When the caller renders its own header (HomeDocumentariesRail
  // does this so it can place a pill bar between the title and the cards),
  // suppress the built-in header row entirely.
  const showHeader = title || viewAllHref || showShuffle;

  return (
    <section className="relative px-6 lg:px-10 py-6 group/row fm-fade" data-testid={testid}>
      {showHeader && (
      <div className="flex items-baseline justify-between mb-4 max-w-[1440px] mx-auto gap-4">
        <div className="min-w-0">
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-baseline flex-wrap gap-x-3 gap-y-1">
            <span>{title}</span>
            {badge && (
              <span
                className="text-[10px] uppercase tracking-wider font-semibold border border-white/10 bg-white/5 text-neutral-300 rounded-full px-2 py-0.5 align-middle whitespace-nowrap"
                data-testid={`${testid}-badge`}
              >
                {badge}
              </span>
            )}
          </h2>
          {subtitle && <p className="text-sm text-neutral-500 mt-1">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {showShuffle && (
            <button
              onClick={handleRefresh}
              disabled={spinning}
              data-testid={`${testid}-refresh`}
              aria-label="Shuffle picks"
              title="Show me different picks"
              className="group/btn flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 hover:border-[#E50914]/60 hover:bg-[#E50914]/10 text-neutral-300 hover:text-white transition-colors text-xs font-semibold"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${spinning ? "animate-spin" : "group-hover/btn:rotate-180 transition-transform duration-500"}`} />
              <span className="hidden sm:inline">Shuffle</span>
            </button>
          )}
          {viewAllHref && (
            <Link
              to={viewAllHref}
              data-testid={`${testid}-viewall`}
              className="flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.15em] text-neutral-400 hover:text-white transition-colors whitespace-nowrap"
            >
              View all <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>
      </div>
      )}

      <div className="relative max-w-[1440px] mx-auto">
        {/* Edge fade gradients */}
        {canLeft && <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-[#0A0A0A] to-transparent z-[5]" />}
        {canRight && <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-[#0A0A0A] to-transparent z-[5]" />}

        {canLeft && (
          <button
            onClick={() => scroll(-1)}
            aria-label="scroll left"
            data-testid={`${testid}-scroll-left`}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-black/80 hover:bg-[#E50914] border border-white/15 backdrop-blur flex items-center justify-center transition-all opacity-70 hover:opacity-100"
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
        )}
        {canRight && (
          <button
            onClick={() => scroll(1)}
            aria-label="scroll right"
            data-testid={`${testid}-scroll-right`}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-black/80 hover:bg-[#E50914] border border-white/15 backdrop-blur flex items-center justify-center transition-all opacity-70 hover:opacity-100"
          >
            <ChevronRight className="w-5 h-5 text-white" />
          </button>
        )}

        <div ref={ref} className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 scroll-smooth">
          {(isPro ? items : items.slice(0, FREE_RAIL_CAP)).map((it, idx) => (
            <MovieCard key={`${it.media_type}-${it.tmdb_id}-${idx}`} item={it} showLessVariant={showLessVariant} showPlatform={showPlatform} priority={idx < 6} />
          ))}
          {/* v2.97o — Free-tier "See all → Go Pro" tile, only rendered when
              the rail was actually truncated by the cap. Pro users never see this. */}
          {!isPro && items.length > FREE_RAIL_CAP && (
            <button
              type="button"
              onClick={openPaywall}
              data-testid={`${testid || "rail"}-go-pro-tile`}
              className="shrink-0 w-44 sm:w-48 rounded-xl border border-amber-400/30 bg-gradient-to-br from-amber-400/10 to-orange-500/5 hover:border-amber-400/60 transition flex flex-col items-center justify-center p-6 gap-3 group"
            >
              {/* Lock icon with floating "Unlock all N" badge */}
              <div className="relative">
                <Lock className="w-7 h-7 text-amber-400 group-hover:scale-110 transition" />
                <span className="absolute -top-2.5 -right-8 whitespace-nowrap bg-amber-400 text-zinc-900 text-[9px] font-extrabold tracking-wide px-1.5 py-0.5 rounded-full leading-tight shadow-md">
                  Unlock all {totalCount || items.length}
                </span>
              </div>
              <div className="text-center">
                <div className="text-sm font-semibold text-white">
                  See all {totalCount || items.length} titles
                </div>
                <div className="text-xs text-amber-400 mt-1">
                  Go Pro →
                </div>
              </div>
            </button>
          )}
          {/* v3.32 — Free-tier nav tile for small collections (all items fit within
              FREE_RAIL_CAP so no paywall tile is shown, but the collection page may
              have more context / allow full browsing). Only visible when a destination
              exists (collectionSlug or viewAllHref). */}
          {!isPro && (collectionSlug || viewAllHref) && items.length <= FREE_RAIL_CAP && items.length > 0 && (
            <Link
              to={viewAllHref || `/collection/${collectionSlug}`}
              data-testid={`${testid || "rail"}-full-collection-tile`}
              className="shrink-0 w-44 sm:w-48 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] hover:border-white/20 transition-all flex flex-col items-center justify-center p-6 gap-3 group"
            >
              <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 group-hover:border-white/25 group-hover:bg-white/10 transition flex items-center justify-center">
                <ArrowRight className="w-5 h-5 text-neutral-400 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
              </div>
              <div className="text-center">
                <div className="text-sm font-semibold text-neutral-300 group-hover:text-white transition">Full Collection</div>
                <div className="text-xs text-neutral-500 mt-0.5 group-hover:text-neutral-400 transition">See all →</div>
              </div>
            </Link>
          )}
          {/* v3.31 — Pro "See More" end-card. Shows for any rail that has
              either a collectionSlug (genre collections) or a viewAllHref
              (list-based rails like Trending India, Hidden Gems, etc.). */}
          {isPro && (collectionSlug || viewAllHref) && items.length > 0 && (
            <Link
              to={viewAllHref || `/collection/${collectionSlug}`}
              data-testid={`${testid || "rail"}-view-more-tile`}
              className="shrink-0 w-44 sm:w-48 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] hover:border-white/20 transition-all flex flex-col items-center justify-center p-6 gap-3 group"
            >
              <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 group-hover:border-white/25 group-hover:bg-white/10 transition flex items-center justify-center">
                <ArrowRight className="w-5 h-5 text-neutral-400 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
              </div>
              <div className="text-center">
                <div className="text-sm font-semibold text-neutral-300 group-hover:text-white transition">See More</div>
                {totalCount && totalCount > items.length ? (
                  <div className="text-xs text-neutral-500 mt-0.5 group-hover:text-neutral-400 transition">
                    +{totalCount - items.length} more
                  </div>
                ) : (
                  <div className="text-xs text-neutral-600 mt-0.5">Full Collection</div>
                )}
              </div>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
